import { aimAt, hintFor, layoutFor, movePlayer, type Hint, type Layout, type Pose } from "../game/kitchen";
import type { Restaurant } from "../game/restaurants";
import type { Settings } from "../game/settings";
import { POUR_TAP, act, createShift, machineStatus, tick, type ShiftState } from "../game/shift";
import { KitchenScene } from "../render/KitchenScene";
import { cuesFor } from "../sound/cues";
import { Sfx } from "../sound/sfx";
import { Controls } from "./controls";

// One shift, played: each frame reads the controls, walks you, runs the
// shift's clock, works out what you're aiming at, applies a Use, and draws.
// React gets a snapshot for the HUD a few times a second and after every Use.

export interface HudSnapshot {
  state: ShiftState;
  hint: Hint | null;
  /** The mouse isn't taken for looking yet (desktop): click the kitchen. */
  needsClick: boolean;
}

export interface EngineCallbacks {
  onHud: (hud: HudSnapshot) => void;
  /** The mouse was let go (Esc), so the game should pause. */
  onUnlock: () => void;
}

/** While Use is held on a dirty dish, a sponge stroke (its swish and swipe) this often. */
const STROKE = 0.28;
const HUD_EVERY = 0.1;
/** A footstep every this many metres walked. */
const STRIDE = 0.75;

export class Engine {
  private state: ShiftState;
  private readonly layout: Layout;
  private pose: Pose;
  private readonly scene: KitchenScene;
  readonly controls: Controls;
  private running = false;
  private frame = 0;
  private last = 0;
  private hudAt = 0;
  private scrubbedAt = -10;
  /** When an order last went out the window, for the bell. */
  private rungAt = -10;
  /** When the sponge last did any work. */
  private lastScrubAt = -10;
  private walking = 0;
  private strideLeft = STRIDE;
  /** When the last dish came clean, for the sparkle. */
  private cleanAt = -10;
  /** When something jolted the view (the hood slamming, a crash). */
  private joltAt = -10;
  private resizeObserver: ResizeObserver | null = null;
  readonly sfx: Sfx;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    restaurant: Restaurant,
    seed: number,
    private readonly callbacks: EngineCallbacks,
    settings: Settings,
  ) {
    this.state = createShift(restaurant.setup!, seed);
    this.layout = layoutFor(restaurant.setup!);
    this.pose = this.layout.start;
    this.sfx = new Sfx(settings.volume);
    this.scene = new KitchenScene(canvas, restaurant, this.layout);
    this.controls = new Controls(canvas, callbacks.onUnlock);
    this.applySettings(settings);
    this.resize();
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(canvas);
    }
    this.last = performance.now();
    this.frame = requestAnimationFrame(this.loop);
    // `#debug` on the URL lets a script stand you somewhere (screenshots, smoke tests).
    if (location.hash === "#debug") (window as unknown as { orderup: unknown }).orderup = { setPose: (p: Pose) => (this.pose = p), state: () => this.state, setState: (s: ShiftState) => (this.state = s), drawCalls: () => this.scene.drawCalls() };
  }

  applySettings(settings: Settings): void {
    this.sfx.volume = settings.volume;
    this.controls.lookSpeed = settings.lookSpeed;
    this.controls.invertY = settings.invertY;
  }

  /** Start or stop the shift's clock and the controls; the kitchen keeps drawing either way. */
  setRunning(running: boolean): void {
    this.running = running;
    this.controls.enabled = running && !this.state.over;
    if (!running) this.controls.unlock();
  }

  dispose(): void {
    cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
    this.controls.dispose();
    this.scene.dispose();
    this.sfx.dispose();
  }

  private resize(): void {
    const { clientWidth: w, clientHeight: h } = this.canvas;
    if (w && h) this.scene.resize(w, h);
  }

  private readonly loop = (now: number) => {
    const dt = Math.min(0.1, Math.max(0, (now - this.last) / 1000));
    this.last = now;
    const time = now / 1000;
    let acted = false;

    const frameStart = this.state;
    if (this.running && !this.state.over) {
      const input = this.controls.read();
      const before = this.pose;
      this.pose = movePlayer(this.layout, this.pose, input, dt);
      const dist = Math.hypot(this.pose.x - before.x, this.pose.z - before.z);
      const moved = dist / Math.max(dt, 1e-3);
      this.walking += ((moved > 0.3 ? 1 : 0) - this.walking) * Math.min(1, dt * 8);
      this.strideLeft -= dist;
      if (this.strideLeft <= 0) {
        this.strideLeft = STRIDE;
        this.sfx.step();
      }
      this.state = tick(this.state, dt);

      let uses = this.controls.takeUses();
      let hint = hintFor(aimAt(this.layout, this.pose, this.state), this.state);
      // Scrubbing goes as long as Use is held on the dirty dish, not per press.
      // A tap is a short stroke, but never more than the time since the
      // last one, so tapping can't beat holding.
      if (hint?.action?.type === "scrub") {
        const seconds = this.controls.useHeld ? dt : uses ? Math.min(STROKE / 2, time - this.lastScrubAt) : 0;
        uses = 0;
        if (seconds > 0) {
          this.state = act(this.state, { type: "scrub", seconds });
          this.lastScrubAt = time;
          acted = true;
          if (time - this.scrubbedAt >= STROKE) {
            this.scrubbedAt = time;
            this.sfx.play("scrub");
          }
        }
      }
      // Pouring is held too: the cup fills while Use is held under the nozzle; a tap is a splash.
      // Grinding is held the same way.
      if (hint?.action?.type === "pour" || hint?.action?.type === "grind") {
        const seconds = this.controls.useHeld ? dt : uses ? POUR_TAP : 0;
        uses = 0;
        if (seconds > 0) {
          this.state = act(this.state, { ...hint.action, seconds });
          acted = true;
        }
      }
      this.sfx.setPouring(hint?.action?.type === "pour" && this.controls.useHeld);
      this.sfx.setGrinding(hint?.action?.type === "grind" && this.controls.useHeld);
      for (; uses > 0 && hint?.action && hint.action.type !== "scrub" && hint.action.type !== "pour" && hint.action.type !== "grind"; uses--) {
        this.state = act(this.state, hint.action);
        acted = true;
        hint = hintFor(aimAt(this.layout, this.pose, this.state), this.state);
      }
      if (this.state.over) {
        this.controls.enabled = false;
        this.controls.unlock();
      }
    } else {
      this.controls.read();
      this.walking = 0;
    }

    for (const cue of cuesFor(frameStart, this.state)) {
      this.sfx.play(cue);
      if (cue === "clean") this.cleanAt = time;
      if (cue === "machineStart" || cue === "crash") this.joltAt = time;
      if (cue === "bell") this.rungAt = time;
    }
    this.sfx.setAmbience(this.running ? 1 : 0, machineStatus(this.state) === "washing");
    // The coffee truck borrows the loops: the wand's steam hisses like the flat-top, shots drip like the fryer bubbles.
    const st = this.state;
    this.sfx.setCooking(st.griddle.filter(Boolean).length + (st.wand ? 2 : 0), st.fryer.filter(Boolean).length + st.groups.filter(Boolean).length * 0.5, this.running);

    const aim = this.running ? aimAt(this.layout, this.pose, this.state) : null;
    this.scene.sync(this.state, { pose: this.pose, aim, time, walking: this.walking, scrubbedAt: this.scrubbedAt, cleanAt: this.cleanAt, joltAt: this.joltAt, rungAt: this.rungAt });
    this.scene.render();

    if (acted || time - this.hudAt >= HUD_EVERY) {
      this.hudAt = time;
      this.callbacks.onHud({ state: this.state, hint: hintFor(aim, this.state), needsClick: this.controls.needsClick });
    }
    this.frame = requestAnimationFrame(this.loop);
  };
}
